<template>
  <MultiLevelNavigationModal
    v-model="localValue"
    :modal-title="$t('selectErpAttribute')"
    :fetch-branches="getERPSection"
    :fetch-leaves="getErpAttribute"
    :selectable-type="['leaf']"
    :selected-entry-id="selectedEntryId"
    :selected-path="selectedPath"
    :base-path-title="$t('erpAttributes')"
    with-all
    @confirm="onConfirm"
    @hide="emits('hide')"
    @cancel="emits('cancel')"
    @close="emits('close')" />
</template>

<script setup lang="ts">
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { ERPSection } from "@medapro-types/ContentEditor/Components/ExcelEditEditor";
import { ErpAttributeModel } from "@medapro-types/common/common";
import { Branch, Leaf, MultiLevelNavigationConfirmModel } from "@medapro-types/common/components/MultiLevelNavigation";
import erpAttributesApi from "@services/erpAttributes.js";
import erpSectionsApi from "@services/erpSections.js";
import { computed } from "vue";
import MultiLevelNavigationModal from "./MultiLevelNavigationModal.vue";

const BRANCH_ICON = "Section";
const LEAF_ICON = "Attribute";

//#region General
const emits = defineEmits(["confirm", "cancel", "close", "input", "hide"]);

const props = defineProps<{
  value: boolean;
  selectedEntryId: string | null | undefined;
  selectedPath: Branch[] | null | undefined;
}>();
//#endregion

//#region Store
const { primaryLanguageCode } = useContentLanguages();
//#endregion

const onConfirm = (event: MultiLevelNavigationConfirmModel) => {
  emits("confirm", event);
};

//#region localValue
const localValue = computed({
  get: () => props.value,
  set: (nv) => emits("input", nv)
});

//#endregion

//#region FetchMethods
const getERPSection = async (groupId: string | null | undefined): Promise<Branch[]> => {
  if (groupId || groupId === "") return [];
  const fetchResult: Record<string, ERPSection>[] = await erpSectionsApi.get([primaryLanguageCode.value]);

  return fetchResult.map((x) => ({
    id: x[primaryLanguageCode.value].erpSectionId,
    depth: 0,
    name: x[primaryLanguageCode.value].name,
    parentId: "",
    icon: BRANCH_ICON
  }));
};

const getErpAttribute = async (sectionId: string | null | undefined): Promise<Leaf[]> => {
  if (!sectionId) return [];
  const fetchResult: Record<string, ErpAttributeModel>[] = await erpAttributesApi.get([primaryLanguageCode.value], sectionId);
  return fetchResult.map((x) => ({
    id: x[primaryLanguageCode.value].erpAttributeId,
    name: x[primaryLanguageCode.value].name,
    dataType: x[primaryLanguageCode.value].dataType,
    icon: LEAF_ICON
  }));
};
//#endregion
</script>
